import { useMemo, useState } from 'react'
import { useCompanies, useDirectory, useProjects, useTasks } from '../api/hooks'
import { useAuth } from '../auth/AuthProvider'
import { TaskDrawer } from '../components/TaskDrawer'
import { Avatar } from '../components/TaskRow'
import { describeDueDate } from '../lib/dates'
import type { Task, TaskStatus } from '../api/types'
import {
  Chip,
  EmptyState,
  ErrorNotice,
  Loading,
  PageHeader,
  PriorityFlag,
  TASK_STATUS_LABEL,
  cx,
} from '../components/ui'

const COLUMNS: TaskStatus[] = ['TODO', 'IN_PROGRESS', 'BLOCKED', 'REVIEW', 'DONE']

const DUE_TONE = {
  danger: 'text-red-600 dark:text-red-400',
  warn: 'text-amber-600 dark:text-amber-400',
  muted: 'text-slate-500 dark:text-slate-400',
} as const

export function BoardPage() {
  const { user } = useAuth()
  const [companyId, setCompanyId] = useState('')
  const [projectId, setProjectId] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [search, setSearch] = useState('')
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)

  const { data: companies } = useCompanies()
  const { data: projects } = useProjects()
  const { data: people } = useDirectory()

  const { data: tasks, isLoading, error } = useTasks({
    companyId: companyId || undefined,
    projectId: projectId || undefined,
    assigneeId: assigneeId || undefined,
    q: search.trim() || undefined,
    includeDone: true,
  })

  const columns = useMemo(() => {
    const grouped = new Map<TaskStatus, Task[]>(COLUMNS.map((s) => [s, []]))
    for (const task of tasks ?? []) {
      grouped.get(task.status)?.push(task)
    }
    return grouped
  }, [tasks])

  // Projects filter follows the company filter, so the two cannot contradict.
  const visibleProjects = companyId
    ? (projects ?? []).filter((p) => p.company.id === companyId)
    : (projects ?? [])

  return (
    <>
      <PageHeader
        title="Board"
        subtitle="Everything you can see, by stage. Filter down to one entity or one person."
      />

      <div className="card mb-5 grid gap-2 p-3 sm:grid-cols-2 lg:grid-cols-4">
        <select
          className="field"
          value={companyId}
          onChange={(e) => {
            setCompanyId(e.target.value)
            setProjectId('')
          }}
          aria-label="Filter by company"
        >
          <option value="">All companies</option>
          {companies?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <select
          className="field"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          aria-label="Filter by project"
        >
          <option value="">All projects</option>
          {visibleProjects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>

        <select
          className="field"
          value={assigneeId}
          onChange={(e) => setAssigneeId(e.target.value)}
          aria-label="Filter by assignee"
        >
          <option value="">Everyone</option>
          <option value={user?.id ?? ''}>Just me</option>
          <option value="unassigned">Unassigned</option>
          {people?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>

        <input
          className="field"
          placeholder="Search titles and notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search tasks"
        />
      </div>

      {isLoading && <Loading />}
      {error && <ErrorNotice error={error} />}

      {tasks && tasks.length === 0 && (
        <EmptyState
          title="No tasks match"
          description="Try clearing a filter, or add a task from My work."
        />
      )}

      {tasks && tasks.length > 0 && (
        // Horizontal scroll is confined to the board itself so the page never
        // scrolls sideways on a phone.
        <div className="-mx-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0">
          <div className="flex min-w-max gap-3">
            {COLUMNS.map((status) => {
              const items = columns.get(status) ?? []
              return (
                <section key={status} className="flex w-72 shrink-0 flex-col">
                  <h2 className="mb-2 flex items-center gap-2 px-1 text-sm font-semibold">
                    {TASK_STATUS_LABEL[status]}
                    <span className="rounded-full bg-slate-100 px-1.5 text-xs font-medium text-slate-600 tabular-nums dark:bg-slate-800 dark:text-slate-400">
                      {items.length}
                    </span>
                  </h2>
                  <div className="flex flex-col gap-2 rounded-xl bg-slate-100/70 p-2 dark:bg-slate-900/60">
                    {items.map((task) => (
                      <BoardCard key={task.id} task={task} onOpen={setOpenTaskId} />
                    ))}
                    {items.length === 0 && (
                      <p className="px-1 py-6 text-center text-xs text-slate-400 dark:text-slate-600">
                        Nothing here
                      </p>
                    )}
                  </div>
                </section>
              )
            })}
          </div>
        </div>
      )}

      {openTaskId && <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} />}
    </>
  )
}

function BoardCard({ task, onOpen }: { task: Task; onOpen: (id: string) => void }) {
  const due = describeDueDate(task.dueDate)
  const done = task.status === 'DONE'

  return (
    <button
      type="button"
      onClick={() => onOpen(task.id)}
      className="card w-full p-2.5 text-left transition-shadow hover:shadow-md"
    >
      <p className={cx('text-sm font-medium', done && 'text-slate-400 line-through dark:text-slate-500')}>
        {task.title}
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        {task.company && <Chip>{task.company.shortName ?? task.company.name}</Chip>}
        <PriorityFlag priority={task.priority} />
        {!done && task.dueDate && <span className={DUE_TONE[due.tone]}>{due.text}</span>}
      </div>

      {task.project && (
        <p className="mt-1.5 truncate text-xs text-slate-500 dark:text-slate-400">{task.project.name}</p>
      )}

      <div className="mt-2 flex items-center justify-between">
        {task.assignee ? (
          <Avatar name={task.assignee.name} />
        ) : (
          <span className="text-xs text-slate-400 dark:text-slate-500">Unassigned</span>
        )}
        {task.commentCount > 0 && (
          <span className="text-xs text-slate-400 dark:text-slate-500">{task.commentCount} 💬</span>
        )}
      </div>
    </button>
  )
}
